import { useEffect, useMemo, useRef, useState } from "react"
import { Modal } from "react-bootstrap"
import { getWeddingAPI } from "../../api/weddingApi"
import { InvitationInfoData } from "../../models/invitationsApiTypes"
import { WeddingInfo } from "../../models/weddingApiTypes"
import { toastWarning } from "../../utils/toastMethod"
import AnteprimaSection from "./AnteprimaSection"
import MessaggiGeneratiSection from "./MessaggiGeneratiSection"
import TemplateSection from "./TemplateSection"
import {
  GeneratedInvitationMessage,
  buildPreviewRecord,
  getAvailablePlaceholders,
  resolveInvitationMessage,
} from "../../utils/invitationMessageGenerator"
import "../../style/invitation_message_generator.css"

interface InvitationMessageGeneratorModalProps {
  tenant: string
  show: boolean
  setShow: (show: boolean) => void
  invitations: InvitationInfoData[]
  isLoadingInvitations: boolean
}

const DEFAULT_TEMPLATE =
  "Ciao! Non vediamo l'ora di festeggiare con te. Conferma la tua presenza qui: {rsvpUrlWithPin}"

export default function InvitationMessageGeneratorModal({
  tenant,
  show,
  setShow,
  invitations,
  isLoadingInvitations,
}: InvitationMessageGeneratorModalProps) {
  const [template, setTemplate] = useState<string>(DEFAULT_TEMPLATE)
  const [wedding, setWedding] = useState<WeddingInfo | null>(null)
  const [isWeddingLoading, setIsWeddingLoading] = useState<boolean>(false)
  const [generatedMessages, setGeneratedMessages] = useState<GeneratedInvitationMessage[]>([])
  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({})
  const copyTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  const clearCopyTimeouts = () => {
    Object.values(copyTimeoutsRef.current).forEach((timeoutId) => {
      clearTimeout(timeoutId)
    })
    copyTimeoutsRef.current = {}
  }

  useEffect(() => {
    if (!show) {
      return
    }

    setIsWeddingLoading(true)

    getWeddingAPI(tenant)
      .then((result: WeddingInfo) => {
        setWedding(result)
      })
      .catch(() => {
        setWedding(null)
        toastWarning("Qualcosa è andato storto. Riprova!")
      })
      .finally(() => {
        setIsWeddingLoading(false)
      })
  }, [show, tenant])

  useEffect(() => {
    if (show) {
      return
    }

    setGeneratedMessages([])
    setCopiedStates({})
    clearCopyTimeouts()
  }, [show])

  useEffect(() => {
    return () => {
      clearCopyTimeouts()
    }
  }, [])

  const previewInvitation = useMemo(() => buildPreviewRecord(invitations), [invitations])

  const previewMessage = useMemo(() => {
    if (!previewInvitation) {
      return ""
    }

    return resolveInvitationMessage(template, previewInvitation, tenant, wedding?.frontendUrl)
  }, [previewInvitation, template, tenant, wedding?.frontendUrl])

  const availablePlaceholders = useMemo(() => {
    return getAvailablePlaceholders(wedding?.frontendUrl, previewInvitation, tenant)
  }, [wedding?.frontendUrl, previewInvitation, tenant])

  const isGenerateDisabled =
    template.trim().length === 0 || invitations.length === 0 || isWeddingLoading || isLoadingInvitations

  const handleGenerateMessages = () => {
    const resolvedMessages = invitations.map((invitation) => {
      return {
        invitationId: invitation.id,
        invitationName: invitation.name,
        pin: invitation.reservationCode,
        message: resolveInvitationMessage(template, invitation, tenant, wedding?.frontendUrl),
      }
    })

    setGeneratedMessages(resolvedMessages)
    setCopiedStates({})
    clearCopyTimeouts()
  }

  const handleCopy = async (message: string, key: string) => {
    try {
      await navigator.clipboard.writeText(message)

      setCopiedStates((previousState) => ({
        ...previousState,
        [key]: true,
      }))

      if (copyTimeoutsRef.current[key]) {
        clearTimeout(copyTimeoutsRef.current[key])
      }

      copyTimeoutsRef.current[key] = setTimeout(() => {
        setCopiedStates((previousState) => ({
          ...previousState,
          [key]: false,
        }))
        delete copyTimeoutsRef.current[key]
      }, 1000)
    } catch {
      toastWarning("Impossibile copiare il messaggio. Riprova.")
    }
  }

  return (
    <Modal show={show} onHide={() => setShow(false)} centered={true} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Genera messaggi invito</Modal.Title>
      </Modal.Header>

      <Modal.Body className="invitation-generator-body">
        <p className="intro-text">
          Personalizza un template e genera velocemente un messaggio per ogni invito. Puoi usare i placeholder qui sotto
          per inserire automaticamente i dati dell&apos;invito.
        </p>

        <TemplateSection
          template={template}
          onChange={setTemplate}
          availablePlaceholders={availablePlaceholders}
        />

        <AnteprimaSection previewInvitation={previewInvitation} previewMessage={previewMessage} />

        {!isLoadingInvitations && invitations.length === 0 && (
          <div className="empty-state">Non ci sono inviti da elaborare.</div>
        )}

        {generatedMessages.length > 0 && (
          <MessaggiGeneratiSection
            generatedMessages={generatedMessages}
            copiedStates={copiedStates}
            onCopy={handleCopy}
          />
        )}
      </Modal.Body>

      <Modal.Footer>
        <button type="button" className="btn btn-secondary button-style" onClick={() => setShow(false)}>
          Annulla
        </button>
        <button
          type="button"
          className="btn btn-primary button-style"
          disabled={isGenerateDisabled}
          onClick={handleGenerateMessages}
        >
          Genera inviti
        </button>
      </Modal.Footer>
    </Modal>
  )}
